import { useState, useEffect } from "react";
import { Download, X } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const PWA_DISMISSED_KEY = "pwa-banner-dismissed";

export const PwaInstallBanner = ({ accentColor = "hsl(24,95%,53%)" }: { accentColor?: string }) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed recently
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    const dismissed = localStorage.getItem(PWA_DISMISSED_KEY);
    if (dismissed && Date.now() - Number(dismissed) < 3 * 86400000) return; // 3 days

    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    setIsIOS(ios);

    if (ios) {
      // Show iOS instructions after a short delay
      const t = setTimeout(() => setShow(true), 3000);
      return () => clearTimeout(t);
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setShow(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(PWA_DISMISSED_KEY, String(Date.now()));
  };

  if (!show) return null;

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 text-white text-xs animate-in slide-in-from-top duration-300"
      style={{ backgroundColor: accentColor }}
    >
      <Download className="h-4 w-4 shrink-0" />
      <p className="flex-1">
        {isIOS
          ? "Instale este cardápio: toque em Compartilhar → Adicionar à Tela de Início"
          : "Instale este cardápio como app para acesso rápido!"
        }
      </p>
      {!isIOS && deferredPrompt && (
        <button
          onClick={handleInstall}
          className="shrink-0 px-3 py-1 rounded-full text-[10px] font-bold bg-white/20 hover:bg-white/30 transition-colors"
        >
          Instalar
        </button>
      )}
      <button onClick={handleDismiss} className="shrink-0 p-0.5 rounded-full hover:bg-white/20 transition-colors">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
